import { clamp } from '../../core/math';
import { Timeline } from '../../core/timeline';
import type { BehaviorContext, ThemeBehavior } from '../../core/behavior';
import type { Action, PainAction, Point, Surface } from '../../core/types';
import { PainEffects } from './effects';

/** Gravity techniques own their timelines, visual state and reversible page animations. */
export class PainBehavior implements ThemeBehavior {
  private timeline = new Timeline();
  private visuals = new PainEffects();
  private active: PainAction | undefined;
  private elapsed = 0;
  private pointer: Point | undefined;
  private tracked: HTMLElement | undefined;
  private animations: Animation[] = [];

  constructor(private context: BehaviorContext) {}

  canInterrupt(): boolean {
    return false;
  }

  trigger(action: Action): void {
    this.active = action as PainAction;
    this.elapsed = 0;
    this.visuals.start(this.active);
    switch (this.active) {
      case 'shinratensei':
        this.repel();
        break;
      case 'banshotenin':
        this.attract();
        break;
      case 'chibakutensei':
        this.moon();
        break;
      case 'levitate':
        this.levitate();
        break;
      case 'receivers':
        this.receivers();
        break;
      case 'rinnegan':
        this.gaze();
        break;
      case 'rain':
        this.context.pose('rain');
        this.timeline.after(4.8, this.finish);
        break;
      case 'prepare':
        this.context.pose('prepare');
        this.timeline.after(2.3, () => this.context.pose('gravity'));
        this.timeline.after(4, this.finish);
        break;
    }
  }

  update(dt: number, pointer?: Point): void {
    this.pointer = pointer;
    if (!this.active) return;
    if (this.tracked && !this.context.page.measureSurface(this.tracked)) {
      this.cancel();
      return;
    }
    this.elapsed += dt;
    this.timeline.update(dt);
  }

  reflow(): void {
    if (this.active !== 'rinnegan') return;
    const hero = this.context.hero();
    if (!this.pointer) {
      hero.look = { x: 0, y: Math.sin(this.elapsed * 1.7) * 0.5 };
      return;
    }
    hero.facing = this.pointer.x >= hero.x ? 1 : -1;
    hero.look = {
      x: clamp((this.pointer.x - hero.x) / hero.size, -1, 1),
      y: clamp((this.pointer.y - hero.y + hero.size * 0.87) / hero.size, -1, 1),
    };
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;
    const target = this.context.page.measureSurface(this.tracked);
    this.visuals.draw(ctx, this.context.hero(), this.elapsed, this.context.page.bounds, target);
  }

  cancel(): void {
    if (!this.active) return;
    this.finish();
  }

  private finish = (): void => {
    this.timeline.clear();
    this.visuals.clear();
    for (const animation of this.animations) animation.cancel();
    this.animations = [];
    this.active = undefined;
    this.tracked = undefined;
    const hero = this.context.hero();
    hero.motion = undefined;
    hero.opacity = 1;
    hero.rotation = 0;
    hero.look = undefined;
    hero.surfaceOffsetY = 0;
    const support = this.context.page.measureSurface(hero.surface);
    hero.surface = support?.element;
    hero.y = support?.y ?? this.context.floor();
    this.context.pose(support ? 'perch' : 'idle');
    this.context.complete();
  };

  private surfaces(): Surface[] {
    if (!this.context.interacts()) return [];
    const { page, hero } = this.context;
    const origin = hero();
    return page.surfaces
      .filter(
        (s) =>
          s.x >= 0 &&
          s.x + s.width <= page.bounds.width &&
          s.y > origin.size &&
          s.y + s.height <= this.context.floor(),
      )
      .sort(
        (a, b) =>
          Math.hypot(a.x + a.width / 2 - origin.x, a.y - origin.y) -
          Math.hypot(b.x + b.width / 2 - origin.x, b.y - origin.y),
      );
  }

  private targets(limit: number): Surface[] {
    const targets: Surface[] = [];
    for (const surface of this.surfaces()) {
      if (
        targets.some(
          (s) => s.element.contains(surface.element) || surface.element.contains(s.element),
        )
      )
        continue;
      targets.push(surface);
      if (targets.length === limit) break;
    }
    return targets;
  }

  private affect(surface: Surface, x: number, y: number, duration?: number): void {
    if (!this.context.interacts() || this.context.reducedMotion()) return;
    const animation = this.context.page.gravity(surface.element, x, y, duration);
    if (animation) this.animations.push(animation);
  }

  private repel(): void {
    this.context.pose('prepare');
    this.timeline.after(0.45, () => this.context.pose('repel'));
    this.timeline.after(0.7, () => {
      const hero = this.context.hero();
      for (const surface of this.targets(4)) {
        const x = surface.x + surface.width / 2 - hero.x;
        const y = surface.y + surface.height / 2 - hero.y + hero.size * 0.7;
        const length = Math.max(1, Math.hypot(x, y));
        this.affect(surface, (x / length) * 42, (y / length) * 30);
      }
    });
    this.timeline.after(2.5, this.finish);
  }

  private attract(): void {
    this.context.pose('prepare');
    const target = this.surfaces()[0];
    this.tracked = target?.element;
    this.timeline.after(0.5, () => {
      this.context.pose('attract');
      const current = this.context.page.measureSurface(this.tracked);
      if (!current) return;
      const hero = this.context.hero();
      this.affect(
        current,
        clamp(hero.x + hero.facing * hero.size * 0.45 - current.x - current.width / 2, -150, 150),
        clamp(hero.y - hero.size * 0.7 - current.y - current.height / 2, -90, 90),
        1800,
      );
    });
    this.timeline.after(3.1, this.finish);
  }

  private moon(): void {
    this.context.pose('prepare');
    this.timeline.after(0.65, () => this.context.pose('gravity'));
    this.timeline.after(1.1, () => {
      const hero = this.context.hero();
      for (const surface of this.targets(2))
        this.affect(surface, clamp(hero.x - surface.x - surface.width / 2, -24, 24), -22, 2400);
      this.context.say('Chibaku Tensei.', 3);
    });
    this.timeline.after(6.5, this.finish);
  }

  private levitate(): void {
    const hero = this.context.hero();
    const surfaces = this.surfaces().filter((s) => s.element !== hero.surface);
    const destination = surfaces[Math.floor(Math.random() * surfaces.length)];
    this.tracked = destination?.element;
    const x = destination
      ? destination.x + destination.width / 2
      : this.context.page.bounds.width *
        (hero.x > this.context.page.bounds.width / 2 ? 0.25 : 0.75);
    const motion = this.context.move(
      this.context.fitX(x),
      destination?.y ?? this.context.floor(),
      true,
      destination,
    );
    motion.duration = clamp(
      Math.hypot(motion.to.x - motion.from.x, motion.to.y - motion.from.y) / 150,
      1.2,
      2.8,
    );
    motion.arc = Math.min(60, Math.max(18, Math.min(hero.y, motion.to.y) - hero.size * 1.15));
    motion.quietLanding = true;
    motion.onComplete = () => {
      this.context.pose('perch');
      this.timeline.after(0.7, this.finish);
    };
    this.context.pose('float');
  }

  private receivers(): void {
    this.context.pose('receiver');
    this.tracked = this.surfaces()[0]?.element;
    this.timeline.after(0.65, () => this.context.pose('repel'));
    this.timeline.after(1.15, () => {
      const target = this.context.page.measureSurface(this.tracked);
      if (target) this.affect(target, 0, 6, 600);
    });
    this.timeline.after(3.6, this.finish);
  }

  private gaze(): void {
    this.context.pose('watch');
    this.timeline.after(3.2, this.finish);
  }
}
